/**
 * PricingCoachLimitsFields — coach-typed hard limit per pricing dimension
 * (Goal F, EXP-010 A7b). Rendered inside CalibrationTakesPanel.tsx, beside
 * the strictness dial.
 *
 * "For this check, the limit is this number, whatever my example videos
 * say" — one optional field per dimension the automatic scoring measures.
 * Owner decisions:
 *   L-2: the body-position field is always shown (it is the one dimension
 *        that can fire a result before any example videos are minted —
 *        see the day-one path); the other four appear only once a minted
 *        line exists for at least one of them.
 *   L-3: units are degrees / % of torso height / % of shin length — never
 *        a raw statistic.
 *
 * Each field saves independently via PATCH .../pricing-coach-limits on
 * blur. An empty field clears the typed limit (falls back to the line
 * learned from the coach's example videos, or to no line at all).
 */

import { useEffect, useState } from 'react';
import { savePricingCoachLimit } from '@/services/templateApi';
import type {
  PricingCoachLimitEntry,
  PricingDimName,
  PricingLearnedLimitEntry,
} from '@/types/calibrationTake';

interface PricingCoachLimitsFieldsProps {
  templateId: string;
  pricingCoachLimits: Partial<Record<PricingDimName, PricingCoachLimitEntry>> | null | undefined;
  pricingLearnedLimits: Partial<Record<PricingDimName, PricingLearnedLimitEntry>> | null | undefined;
}

/** Field order: body position first (L-2 — always shown), then the rest. */
const DIM_ORDER: PricingDimName[] = [
  'A_orientation',
  'B_motion',
  'E_head_dip',
  'F_foot_dir',
  'H_heel_rise',
];

const DIM_COPY: Record<PricingDimName, { label: string; what: string }> = {
  A_orientation: {
    label: 'Body position',
    what: "The furthest your athlete's body position can be from your demo "
      + 'before an attempt is marked too far off to grade.',
  },
  B_motion: {
    label: 'Timing',
    what: "The furthest your athlete's movement can drift from your demo's "
      + 'timing before an attempt is marked too far off to grade.',
  },
  E_head_dip: {
    label: 'Head position',
    what: "How much lower your athlete's head can dip than your demo before "
      + 'an attempt is marked too far off to grade.',
  },
  F_foot_dir: {
    label: 'Foot direction',
    what: "How far your athlete's foot direction can turn from your demo "
      + 'before an attempt is marked too far off to grade.',
  },
  H_heel_rise: {
    label: 'Heel lift',
    what: "How much higher your athlete's heel can lift than your demo "
      + 'before an attempt is marked too far off to grade.',
  },
};

/**
 * F-10 (red team): client-side range so a bad value never reaches the
 * server as a 422 the field can only report as "please try again". Degrees
 * dimensions (body position, foot direction) cannot exceed 180 — anything
 * beyond that is not a usable angle limit. The percent-based dimensions
 * (timing, head position, heel lift) have no coach-facing upper bound.
 */
const MAX_LIMIT_BY_DIM: Partial<Record<PricingDimName, number>> = {
  A_orientation: 180,
  F_foot_dir: 180,
};

function unitSuffix(unit: string | undefined): string {
  switch (unit) {
    case 'percent_torso_height':
      return '% of torso height';
    case 'percent_shin_length':
      return '% of shin length';
    default:
      return 'degrees';
  }
}

interface FieldRowProps {
  templateId: string;
  dim: PricingDimName;
  entry: PricingCoachLimitEntry | null | undefined;
  learned: PricingLearnedLimitEntry | null | undefined;
}

function FieldRow({ templateId, dim, entry, learned }: FieldRowProps) {
  const unit = entry?.unit ?? learned?.unit ?? (dim === 'A_orientation' || dim === 'F_foot_dir' ? 'degrees' : 'percent_torso_height');
  const [value, setValue] = useState<string>(entry?.limit != null ? String(entry.limit) : '');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Re-sync when the server-side value changes (e.g. after a page reload or
  // another tab's edit) — but never clobber the athlete's in-progress typing.
  useEffect(() => {
    setValue(entry?.limit != null ? String(entry.limit) : '');
  }, [entry?.limit]);

  const copy = DIM_COPY[dim];
  const suffix = unitSuffix(unit);
  const learnedLimit = learned?.learned_limit ?? null;
  const maxAllowed = MAX_LIMIT_BY_DIM[dim];
  const savedLimit = entry?.limit ?? null;

  const commit = async () => {
    setError(null);
    const trimmed = value.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      setError('Enter a positive number, or leave it empty.');
      return;
    }
    // F-10 (red team): bounds-check client-side so a too-large value never
    // round-trips to the server just to come back as an unhelpful "try
    // again" — tell the coach the actual limit up front.
    if (parsed !== null && maxAllowed != null && parsed > maxAllowed) {
      setError(`Enter a number up to ${maxAllowed}, or leave it empty.`);
      return;
    }
    // F-1 (red team): tabbing through this field re-fires the PATCH on
    // every blur even when nothing changed, which re-invalidates every
    // calibration take on the template each time. Skip the save when the
    // parsed value matches what is already saved — including the
    // empty-field-equals-no-typed-limit case.
    if (parsed === savedLimit) {
      return;
    }
    setSaving(true);
    try {
      await savePricingCoachLimit(templateId, dim, parsed);
    } catch {
      setError('Could not save this limit — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-1">
      <label
        htmlFor={`pricing-coach-limit-${dim}`}
        className="block text-xs font-medium text-text-secondary"
      >
        {copy.label}
      </label>
      <p className="text-[10px] text-text-muted">{copy.what}</p>
      <div className="flex items-center gap-2">
        <input
          id={`pricing-coach-limit-${dim}`}
          data-testid={`pricing-coach-limit-input-${dim}`}
          type="number"
          min="0"
          max={maxAllowed}
          inputMode="decimal"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          placeholder={
            learnedLimit != null ? `Learned from your examples: ${learnedLimit}` : 'No line yet'
          }
          className="w-40 px-2 py-1.5 text-sm rounded-md border border-border-subtle bg-dark-elevated-1 text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40"
        />
        <span className="text-xs text-text-muted">{suffix}</span>
        {saving && <span className="text-[10px] text-text-muted">Saving…</span>}
      </div>
      <p className="text-[10px] text-text-muted">
        Leave empty to use the line learned from your example videos. Takes effect when this
        template&apos;s &quot;too far off to grade&quot; line is switched on.
      </p>
      {error && <p className="text-[10px] text-red-400">{error}</p>}
    </div>
  );
}

export default function PricingCoachLimitsFields({
  templateId,
  pricingCoachLimits,
  pricingLearnedLimits,
}: PricingCoachLimitsFieldsProps) {
  // L-2: the other four fields appear only once ANY dimension has a minted
  // line — pricingLearnedLimits itself is null until a block exists at all.
  const hasLearnedBlock = pricingLearnedLimits != null;
  const dimsToShow = DIM_ORDER.filter((dim) => dim === 'A_orientation' || hasLearnedBlock);

  return (
    <div className="space-y-4" data-testid="pricing-coach-limits-fields">
      <div>
        <p className="text-sm font-medium text-text-secondary">Hard limits you set yourself</p>
        <p className="text-[11px] text-text-muted mt-0.5">
          For any of these checks, you can type the exact line instead of letting it be learned
          from your example videos.
        </p>
      </div>
      {dimsToShow.map((dim) => (
        <FieldRow
          key={dim}
          templateId={templateId}
          dim={dim}
          entry={pricingCoachLimits?.[dim] ?? null}
          learned={pricingLearnedLimits?.[dim] ?? null}
        />
      ))}
    </div>
  );
}
